import { proxy, ref } from 'valtio';
import { ExpansionInfo, ExpansionStyleType } from './components/expansion';

export enum Panel {
  Feedback = 'feedback',
  ResouceTips = 'resourcesTips',
  None = 'none',
  All = 'all'
}

const realPanels: Panel[] = [Panel.Feedback, Panel.ResouceTips];

export type ExpansionInfos = { [id in Panel]?: ExpansionInfo };

type SideBarState = {
  openPanel: Panel;
  expansionInfos: ExpansionInfos;
};

export const sideBarState: SideBarState = proxy({
  openPanel: Panel.None,
  expansionInfos: {
    feedback: { disabled: true },
    resourcesTips: { disabled: true }
  }
});

export const setSideBarOpenPanel = (panel: Panel) => {
  if (panel !== Panel.All) {
    sideBarState.openPanel = panel;
    animatePanel(panel, false, true);
  }
};

const animatePanel = (panel: Panel, animate = false, panelOpen = false) => {
  const expansionInfo = sideBarState.expansionInfos[panel];
  let timeout = expansionInfo?.timeout;

  if (timeout) {
    clearTimeout(timeout);
    timeout = undefined;
  }

  if (animate) {
    const lastOpenTime = expansionInfo?.lastOpenTime;
    if (!lastOpenTime || Date.now() - lastOpenTime > 600000) {
      timeout = setTimeout(() => {
        animatePanel(panel, false);
      }, 15000);
    } else {
      animate = false;
    }
  }

  const newExpansionInfo = {
    ...expansionInfo,
    animate,
    timeout
  };
  if (panelOpen) {
    newExpansionInfo.lastOpenTime = Date.now();
  }

  updateExpansionInfos(panel, newExpansionInfo);
};

export const animateSideBar = (panel: Panel) => {
  const panels: Panel[] = panel === Panel.All ? realPanels : [panel];
  panels.forEach((panel) => {
    animatePanel(panel);
  });
};
const updateExpansionInfos = (panel: Panel, expansionInfo: ExpansionInfo) => {
  const expansionInfos = { ...sideBarState.expansionInfos };
  expansionInfos[panel] = expansionInfo;
  sideBarState.expansionInfos = expansionInfos;
};
export const setSideBarDisabled = (panel: Panel, disabled: boolean) => {
  const panels: Panel[] = panel === Panel.All ? realPanels : [panel];
  panels.forEach((panel) => {
    const expansionInfo = { ...sideBarState.expansionInfos[panel], disabled };
    updateExpansionInfos(panel, expansionInfo);
    animatePanel(panel, false);
  });
};

export const setSideBarStyle = (panel: Panel, style: ExpansionStyleType) => {
  const panels: Panel[] = panel === Panel.All ? realPanels : [panel];
  panels.forEach((panel) => {
    const expansionInfo: ExpansionInfo = {
      ...sideBarState.expansionInfos[panel],
      style
    };
    updateExpansionInfos(panel, expansionInfo);
  });
};

export const setSideBarElement = (panel: Panel, element: JSX.Element) => {
  const elementRef = ref(element);
  const expansionInfo = {
    ...sideBarState.expansionInfos[panel],
    elementRef,
    disabled: !elementRef
  };
  setSideBarOpenPanel(panel);
  updateExpansionInfos(panel, expansionInfo);
};

export const setSideBarCorrect = (element: JSX.Element) => {
  setSideBarElement(Panel.Feedback, element);
  setSideBarStyle(Panel.Feedback, 'Correct');
  setSideBarDisabled(Panel.ResouceTips, true);
};

export const setSideBarSecondTry = (element: JSX.Element) => {
  setSideBarDisabled(Panel.ResouceTips, false);
  setSideBarElement(Panel.Feedback, element);
  setSideBarStyle(Panel.Feedback, 'default');
  animatePanel(Panel.ResouceTips, true);
};

export const setSideBarIncorrect = (element: JSX.Element) => {
  setSideBarDisabled(Panel.ResouceTips, true);
  setSideBarElement(Panel.Feedback, element);
  setSideBarStyle(Panel.Feedback, 'Incorrect');
};

export const setSideBarInfo = (element: JSX.Element) => {
  setSideBarDisabled(Panel.ResouceTips, true);
  setSideBarElement(Panel.Feedback, element);
  setSideBarStyle(Panel.Feedback, 'default');
};
